import React, { ReactElement, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { MenuItem } from "@material-ui/core";

import { CustomerContainer, HeaderDiv } from "pages/customers/styles";
import { HeadingDiv } from "pages/location/styles";
import {
  CancelButtonSec,
  SaveButton,
} from "pages/management/privacy-policy/components/details/styles";
import { WARRANTY } from "constants/route/route-constants";
import { WarrantyObject } from "interfaces/warranty-interfaces";
import { updateBrandModelWarranty } from "services/warranty-services";
import { getApiErrorMessage } from "utils/api-error-utils";
import { resolveWarrantyPdfForApi } from "utils/warranty-pdf-utils";

import EditWarrantyPdfPanel from "../components/edit-warranty-pdf-panel";
import {
  WarrantyContainer,
  TabContent,
  ActionButtonsRow,
  FieldContainer,
  InputLabelText,
  ModelDropdown,
  EditFieldsRow,
  EditField,
  ReadOnlyValue,
} from "../styles";

interface LocationState {
  detail?: string;
  rowData?: WarrantyObject;
  isDefault?: boolean;
}

const WARRANTY_STATUS_OPTIONS = [
  { name: "Active", value: "Active" },
  { name: "Inactive", value: "Inactive" },
];

function EditWarranty(): ReactElement | null {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { enqueueSnackbar } = useSnackbar();

  const rowData = location?.state?.rowData;
  const isDefault = Boolean(location?.state?.isDefault);

  const [warrantyStatus, setWarrantyStatus] = useState(
    rowData?.warrantyStatus?.trim() ? rowData.warrantyStatus : "Active"
  );
  const [standardPdf, setStandardPdf] = useState<string | undefined>(
    rowData?.standardWarrantyPdf
  );
  const [extendedPdf, setExtendedPdf] = useState<string | undefined>(
    rowData?.extendedWarrantyPdf
  );
  const [saveLoading, setSaveLoading] = useState(false);

  const statusValue = useMemo(() => {
    const match = WARRANTY_STATUS_OPTIONS.find(
      (o) => o.value.toLowerCase() === warrantyStatus.trim().toLowerCase()
    );
    return match?.value ?? warrantyStatus;
  }, [warrantyStatus]);

  if (!rowData || rowData.id == null) {
    history.replace(WARRANTY);
    return null;
  }

  const carModel = rowData.carModel?.trim() ?? "";

  const handleCancel = () => {
    history.push(WARRANTY);
  };

  const handleSave = async () => {
    const standardWarrantyPdf = resolveWarrantyPdfForApi(standardPdf, "");
    const extendedWarrantyPdf = resolveWarrantyPdfForApi(extendedPdf, "");

    if (!standardWarrantyPdf && !extendedWarrantyPdf) {
      enqueueSnackbar("Please provide at least one warranty PDF", {
        variant: "warning",
      });
      return;
    }

    setSaveLoading(true);
    try {
      await updateBrandModelWarranty(rowData.id, {
        createdOn: rowData.createdOn,
        updatedOn: rowData.updatedOn,
        isDefault,
        standardWarrantyPdf,
        extendedWarrantyPdf,
        warrantyStatus: statusValue,
        carModel,
      });
      enqueueSnackbar("Warranty updated successfully", { variant: "success" });
      history.push(WARRANTY);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Failed to update warranty"), {
        variant: "error",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <CustomerContainer>
      <WarrantyContainer>
        <HeaderDiv>
          <HeadingDiv>Edit Warranty</HeadingDiv>
        </HeaderDiv>

        <TabContent>
          <EditFieldsRow>
            <EditField>
              <FieldContainer>
                <InputLabelText>Car model</InputLabelText>
                <ReadOnlyValue>{carModel || "N/A"}</ReadOnlyValue>
              </FieldContainer>
            </EditField>
            <EditField>
              {/* <FieldContainer>
                <InputLabelText>Warranty status</InputLabelText>
                <ModelDropdown
                  onChange={(e) =>
                    setWarrantyStatus(String(e.target.value ?? ""))
                  }
                  variant="outlined"
                  value={statusValue}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    getContentAnchorEl: null,
                  }}
                >
                  {WARRANTY_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </ModelDropdown>
              </FieldContainer> */}
            </EditField>
          </EditFieldsRow>

          <EditWarrantyPdfPanel
            standardPdfUrl={standardPdf}
            extendedPdfUrl={extendedPdf}
            onStandardUpload={setStandardPdf}
            onExtendedUpload={setExtendedPdf}
            onStandardRemove={() => setStandardPdf(undefined)}
            onExtendedRemove={() => setExtendedPdf(undefined)}
          />

          <ActionButtonsRow>
            <CancelButtonSec
              variant="outlined"
              color="primary"
              onClick={handleCancel}
            >
              Cancel
            </CancelButtonSec>
            <SaveButton
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? "Saving..." : "Save"}
            </SaveButton>
          </ActionButtonsRow>
        </TabContent>
      </WarrantyContainer>
    </CustomerContainer>
  );
}

export default EditWarranty;
